import { Link } from 'react-router-dom';
import { IconArrowLeft } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import {
    PRIVACY_INTRO,
    PRIVACY_SECTIONS,
    PRIVACY_VERSION_DATE,
} from './privacy/privacyContent';
import './PrivacyPage.scss';

function renderPart(part, key) {
    if (part.type === 'text') {
        return <span key={key}>{part.value}</span>;
    }

    if (part.type === 'link') {
        return (
            <Link key={key} to={part.to}>
                {part.value}
            </Link>
        );
    }

    return null;
}

function IntroParagraph({ paragraph }) {
    if (typeof paragraph === 'string') {
        return <p className="privacy-page__text">{paragraph}</p>;
    }

    return (
        <p className="privacy-page__text">
            {paragraph.parts.map((part, index) => renderPart(part, index))}
        </p>
    );
}

function BulletList({ items }) {
    return (
        <ul className="privacy-page__list">
            {items.map((item) => (
                <li key={item}>{item}</li>
            ))}
        </ul>
    );
}

function ProcessingItem({ item }) {
    return (
        <div className="privacy-page__processing-item">
            <p className="privacy-page__processing-goal">
                <span className="privacy-page__processing-label">Цель:</span>
                {' '}
                {item.goal}
            </p>
            <dl className="privacy-page__processing-fields">
                {item.fields.map((field) => (
                    <div key={field.label} className="privacy-page__processing-row">
                        <dt>{field.label}</dt>
                        <dd>{field.value}</dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}

function PrivacySection({ section }) {
    return (
        <section className="privacy-page__section" aria-labelledby={section.id}>
            <h2 id={section.id} className="privacy-page__section-title">
                {section.title}
            </h2>

            {section.paragraphs?.map((paragraph) => (
                <p key={paragraph} className="privacy-page__text">
                    {paragraph}
                </p>
            ))}

            {section.rights?.map((right) => (
                <div key={right.title} className="privacy-page__right">
                    <h3 className="privacy-page__subheading">{right.title}</h3>
                    <p className="privacy-page__text">{right.text}</p>
                </div>
            ))}

            {section.processingItems && (
                <div className="privacy-page__processing">
                    {section.processingItems.map((item) => (
                        <ProcessingItem key={item.goal} item={item} />
                    ))}
                </div>
            )}

            {section.bullets && <BulletList items={section.bullets} />}

            {section.subheading && (
                <p className="privacy-page__text privacy-page__text--spaced">
                    {section.subheading}
                </p>
            )}

            {section.subBullets && <BulletList items={section.subBullets} />}

            {section.footerParagraphs?.map((paragraph) => (
                <p key={paragraph} className="privacy-page__text">
                    {paragraph}
                </p>
            ))}
        </section>
    );
}

export default function PrivacyPage() {
    const { isAuthenticated } = useAuth();
    const backTo = isAuthenticated ? '/subscriptions' : '/login';
    const backLabel = isAuthenticated ? 'К моим подпискам' : 'На страницу входа';

    return (
        <div className="privacy-page">
            <article className="privacy-page__article">
                <header className="privacy-page__hero">
                    <h1 className="privacy-page__title">Политика конфиденциальности</h1>
                </header>

                <div className="privacy-page__intro">
                    {PRIVACY_INTRO.map((paragraph, index) => (
                        <IntroParagraph key={index} paragraph={paragraph} />
                    ))}
                </div>

                {PRIVACY_SECTIONS.map((section) => (
                    <PrivacySection key={section.id} section={section} />
                ))}

                <footer className="privacy-page__footer">
                    <p className="privacy-page__version">
                        Дата опубликования версии:
                        {' '}
                        {PRIVACY_VERSION_DATE}
                    </p>
                </footer>
            </article>

            <Link to={backTo} className="btn btn--primary btn--fixed privacy-page__back">
                <IconArrowLeft size={18} stroke={1.5} />
                {backLabel}
            </Link>
        </div>
    );
}
